<template>
  <v-dialog
    :value="value"
    content-class="sso-connection-form-dialog"
    persistent
    data-testid="sso-connection-form-dialog"
    @input="$emit('input', $event)"
  >
    <v-card :loading="loading" :disabled="loading" loader-height="2">
      <div
        class="sso-connection-form-dialog__header primary white--text d-flex align-center px-4"
        data-testid="sso-connection-form-dialog__header"
      >
        <span class="subtitle-1 white--text" data-testid="sso-connection-form-dialog__title">
          {{ isEdit ? 'Edit SSO connection' : 'Add SSO connection' }}
        </span>
        <v-spacer />
        <v-btn
          icon
          class="white--text"
          :disabled="loading"
          data-testid="sso-connection-form-dialog__close"
          @click="$emit('input', false)"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-card-text class="pt-4" data-testid="sso-connection-form-dialog__form">
        <v-text-field
          v-model="form.displayName"
          label="Display name*"
          outlined
          dense
          hide-details="auto"
          class="mb-3"
          :error-messages="errors.displayName"
          data-testid="sso-connection-form-dialog__display-name"
        />
        <v-text-field
          v-model="form.publicConnectionId"
          label="Public connection ID"
          outlined
          dense
          hide-details="auto"
          class="mb-3"
          :hint="isEdit ? '' : 'Leave blank to auto-generate'"
          :persistent-hint="!isEdit"
          data-testid="sso-connection-form-dialog__public-connection-id"
        />
        <v-text-field
          v-model="form.cognitoProviderName"
          label="Cognito provider name"
          outlined
          dense
          hide-details="auto"
          class="mb-3"
          :hint="isEdit ? '' : 'Leave blank to auto-generate'"
          :persistent-hint="!isEdit"
          data-testid="sso-connection-form-dialog__cognito-provider-name"
        />
        <v-select
          v-model="form.protocol"
          :items="protocolItems"
          label="Protocol"
          outlined
          dense
          hide-details="auto"
          class="mb-3"
          data-testid="sso-connection-form-dialog__protocol"
        />
        <v-radio-group
          v-model="form.metadataType"
          row
          class="mt-0 mb-2"
          hide-details
          data-testid="sso-connection-form-dialog__metadata-type"
        >
          <v-radio label="Metadata URL" value="URL" data-testid="sso-connection-form-dialog__metadata-url-radio" />
          <v-radio label="Metadata XML" value="XML" data-testid="sso-connection-form-dialog__metadata-xml-radio" />
        </v-radio-group>
        <v-text-field
          v-if="form.metadataType === 'URL'"
          v-model="form.metadataUrl"
          label="Metadata URL*"
          outlined
          dense
          hide-details="auto"
          class="mb-3"
          :error-messages="errors.metadata"
          data-testid="sso-connection-form-dialog__metadata-url"
        />
        <v-textarea
          v-else
          v-model="form.metadataXml"
          label="Metadata XML*"
          outlined
          dense
          rows="5"
          hide-details="auto"
          class="mb-3"
          :error-messages="errors.metadata"
          data-testid="sso-connection-form-dialog__metadata-xml"
        />
        <v-text-field
          v-model="form.emailClaim"
          label="Email attribute claim"
          outlined
          dense
          hide-details="auto"
          data-testid="sso-connection-form-dialog__email-claim"
        />
        <div class="caption grey--text mt-1" data-testid="sso-connection-form-dialog__hostname">
          Hostname: {{ hostname || '—' }}
        </div>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          text
          :disabled="loading"
          data-testid="sso-connection-form-dialog__cancel"
          @click="$emit('input', false)"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          data-testid="sso-connection-form-dialog__submit"
          @click="submit"
        >
          {{ isEdit ? 'Save' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import constants from '@/plugins/constants';

const { SSO } = constants;

function emptyForm() {
  return {
    displayName: '',
    publicConnectionId: '',
    cognitoProviderName: '',
    protocol: 'SAML',
    metadataType: 'URL',
    metadataUrl: '',
    metadataXml: '',
    emailClaim: SSO.DEFAULT_EMAIL_CLAIM,
  };
}

function formFromConnection(connection) {
  const source = connection?.metadataSource || {};
  const type = String(source.type || (source.xml ? 'XML' : 'URL')).toUpperCase();
  return {
    displayName: connection?.displayName || connection?.name || '',
    publicConnectionId: connection?.publicConnectionId || '',
    cognitoProviderName: connection?.cognitoProviderName || '',
    protocol: connection?.protocol || 'SAML',
    metadataType: type === 'XML' ? 'XML' : 'URL',
    metadataUrl: source.url || connection?.metadataUrl || '',
    metadataXml: source.xml || '',
    emailClaim:
      connection?.attributeMapping?.email || SSO.DEFAULT_EMAIL_CLAIM,
  };
}

export default {
  name: 'ConnectionFormDialog',
  props: {
    value: { type: Boolean, default: false },
    hostname: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    connection: { type: Object, default: null },
  },
  data() {
    return {
      form: emptyForm(),
      errors: {},
      protocolItems: [{ text: 'SAML', value: 'SAML' }],
    };
  },
  computed: {
    isEdit() {
      return Boolean(
        this.connection?.publicConnectionId ||
          this.connection?._id ||
          this.connection?.id,
      );
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.form = this.connection ? formFromConnection(this.connection) : emptyForm();
        this.errors = {};
      }
    },
  },
  methods: {
    submit() {
      const errors = {};
      const displayName = String(this.form.displayName || '').trim();
      if (!displayName) errors.displayName = 'Display name is required';
      if (!this.hostname) errors.metadata = 'Hostname is required before creating a connection';

      let metadataSource = null;
      if (this.form.metadataType === 'URL') {
        const url = String(this.form.metadataUrl || '').trim();
        if (!url) errors.metadata = 'Metadata URL is required';
        else metadataSource = { type: 'URL', url };
      } else {
        const xml = String(this.form.metadataXml || '').trim();
        if (!xml) errors.metadata = 'Metadata XML is required';
        else metadataSource = { type: 'XML', xml };
      }

      this.errors = errors;
      if (Object.keys(errors).length) return;

      const payload = {
        hostname: this.hostname,
        protocol: this.form.protocol || 'SAML',
        displayName,
        metadataSource,
        attributeMapping: {
          email: String(this.form.emailClaim || SSO.DEFAULT_EMAIL_CLAIM).trim(),
        },
      };
      const publicConnectionId = String(this.form.publicConnectionId || '').trim();
      const cognitoProviderName = String(this.form.cognitoProviderName || '').trim();
      if (publicConnectionId) payload.publicConnectionId = publicConnectionId;
      if (cognitoProviderName) payload.cognitoProviderName = cognitoProviderName;

      this.$emit('submit', payload);
    },
  },
};
</script>

<style lang="scss">
@import './ConnectionFormDialog.scss';
</style>
